import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface FeaturesProps {
  isDarkMode: boolean;
}

const FEATURES = [
  {
    icon: 'ri-smartphone-line',
    label: '01 / Mobile',
    title: 'Mobile utility apps.',
    desc: 'High-performance cleaner apps, system optimisers, battery savers, and file managers for iOS and Android — built for speed and retention.',
  },
  {
    icon: 'ri-computer-line',
    label: '02 / Desktop',
    title: 'Desktop utilities.',
    desc: 'Powerful disk cleaners, duplicate finders, uninstallers, and productivity tools for macOS and Windows with polished native UIs.',
  },
  {
    icon: 'ri-robot-2-line',
    label: '03 / AI Services',
    title: 'AI-powered tools.',
    desc: 'Chatbots, image generators, writing assistants, and smart automation services — integrating the latest AI models into intuitive user experiences.',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    label: '04 / Marketing',
    title: 'App promotion & UA.',
    desc: 'Full-cycle user acquisition strategy: ASO, media buying, influencer campaigns, and app store optimisation to scale your install base.',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    label: '05 / Consulting',
    title: 'IT consulting.',
    desc: 'Technology audits, architecture decisions, tooling recommendations, and process optimisation for teams building utility applications.',
  },
  {
    icon: 'ri-advertisement-line',
    label: '06 / Advertising',
    title: 'Ad creative & media buying.',
    desc: 'Performance-driven ad creatives, A/B testing, and multi-channel media buying — from Meta and Google to TikTok and programmatic networks.',
  },
];

const Features = ({ isDarkMode }: FeaturesProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsWrapRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (headerRef.current) {
        gsap.from(headerRef.current.querySelectorAll('[data-anim]'), {
          opacity: 0,
          y: 30,
          duration: 1,
          ease: 'power3.out',
          stagger: 0.08,
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 75%',
          },
        });
      }
      if (cardsWrapRef.current) {
        gsap.from(cardsWrapRef.current.querySelectorAll('[data-card]'), {
          opacity: 0,
          y: 40,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.06,
          scrollTrigger: {
            trigger: cardsWrapRef.current,
            start: 'top 80%',
          },
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="features"
      className={`relative w-full py-32 md:py-44 px-6 md:px-12 transition-colors duration-700 ${
        isDarkMode ? 'bg-[#0a0a0a] text-white' : 'bg-white text-black'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className="max-w-3xl mb-20 md:mb-28">
          <div
            data-anim
            className={`mb-8 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-mono uppercase tracking-[0.2em] ${
              isDarkMode
                ? 'border-white/20 text-white/70 bg-white/5'
                : 'border-black/15 text-black/60 bg-black/5'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            What we do
          </div>
          <h2
            data-anim
            className="text-[3rem] md:text-[5rem] lg:text-[6rem] leading-[0.9] font-bold tracking-tighter mb-8"
          >
            Six ways to <span className="italic font-light">scale your product</span>.
          </h2>
          <p
            data-anim
            className={`text-base md:text-lg max-w-xl leading-relaxed ${
              isDarkMode ? 'text-white/60' : 'text-black/60'
            }`}
          >
            Every project at Solvarion is built around your product's lifecycle — from code to market. Pick the service that fits, or reach out and we'll design a custom roadmap for your app.
          </p>
        </div>

        <div
          ref={cardsWrapRef}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px ${
            isDarkMode ? 'bg-white/10' : 'bg-black/10'
          }`}
        >
          {FEATURES.map((f) => (
            <div
              key={f.label}
              data-card
              className={`group relative p-8 md:p-10 transition-colors duration-300 ${
                isDarkMode
                  ? 'bg-[#0a0a0a] hover:bg-[#141414]'
                  : 'bg-white hover:bg-[#fafafa]'
              }`}
            >
              <div className="flex items-center justify-between mb-10">
                <div
                  className={`w-12 h-12 flex items-center justify-center rounded-full border ${
                    isDarkMode ? 'border-white/20' : 'border-black/15'
                  }`}
                >
                  <i className={`${f.icon} text-xl`}></i>
                </div>
                <span
                  className={`text-[10px] font-mono uppercase tracking-[0.25em] ${
                    isDarkMode ? 'text-white/40' : 'text-black/40'
                  }`}
                >
                  {f.label}
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4 leading-tight">
                {f.title}
              </h3>
              <p
                className={`text-sm md:text-[15px] leading-relaxed ${
                  isDarkMode ? 'text-white/55' : 'text-black/55'
                }`}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;